import { useEffect, useState } from "react";
import styles from "./TransferMessages.module.css";
import { UseQueryResult } from "@tanstack/react-query";
import {
  DebtDisclosureWarning,
  GetAccountDetailsResponse,
} from "../../lib/api/models";
import { useChannelState } from "../../lib/channelContext";
import WarningBanner from "../WarningBanner";
import classNames from "classnames";

const strings = {
  a0373: "A_0373 - WARNING: Unreported Debt Account.",
  a0374:
    "A_0374 - WARNING: State law CANNOT solicit payments. Time Barred Account.",
  a0375: "A_0375 - WARNING: Time Barred Account.",
  a0376:
    "A_0376 - WARNING: State law CANNOT solicit payments. Time Barred and Unreported Debt Account.",
  a0377: "A_0377 - WARNING: Time Barred and Unreported Debt Account.",
  a0110:
    "A_0110 - This account has been reported lost. Please check for the new account number and work the new account. If a new account is not found, refer the caller to the fraud department.",
  a0111:
    "A_0111 - This account has been reported as having fraudulent activity. Refer the caller to the fraud department.",
  a0112:
    "A_0112 - This account has been charged off. Refer the caller to the fraud department.",
  a0113:
    "A_0113 - This account has been charged off. Refer the caller to the CRU department.",
  a0044: "A_0044: External status of A. Please Research and Resolve.",
  a0045a: "A_0045: Transfer call to Collections - Extension COLL-OPEN_TRANSFER",
  a0045b: "A_0045: Transfer call to Collections - Extension COLL-CRU_TRANSFER",
  bankrupt: "Account Closed: Bankrupt or Interest Prohibited",
  chargedOff: "Account Closed: Charged Off",
  massachusetts: "C_09023 - State is MASSACHUSETTS",
  newYork: "C_09022 - NEW YORK CITY Account",
  vipAccount:
    "A_0500 - WARNING: VIP Account. Transfer call to extension CS_US_SUP",
};

type Props = {
  accountDetailsQueryResult: UseQueryResult<GetAccountDetailsResponse>;
  debtDisclosureWarning?: DebtDisclosureWarning | null;
};

export default function TransferMessages({
  accountDetailsQueryResult: { data, isLoading, isError },
  debtDisclosureWarning,
}: Props) {
  const [visibleMessages, setVisibleMessages] = useState<string[]>([]);
  const { channel } = useChannelState();

  useEffect(() => {
    if (!data) {
      return;
    }

    const messages: Set<string> = new Set();

    if (data.externalStatusCode === "U") {
      messages.add(strings.a0111);
    } else if (data.externalStatusCode === "Z") {
      messages.add(strings.chargedOff);
    }

    if (
      data.externalStatusCode === "Z" &&
      data.permanentQueueCode !== "088" &&
      data.permanentQueueCode !== "778"
    ) {
      messages.add(strings.a0113);
    } else if (
      data.externalStatusCode === "Z" &&
      (data.permanentQueueCode === "088" || data.permanentQueueCode === "778")
    ) {
      messages.add(strings.a0112);
    }

    if (
      data.internalStatusCode === "O" &&
      data.csrDataPanel.ocl10PercentOrGreater === "Yes"
    ) {
      if (data.delinquentBucket === "210/PCO") {
        messages.add(strings.a0045b);
      } else {
        messages.add(strings.a0045a);
      }
    }

    if (
      (data.internalStatusCode === "" ||
        data.internalStatusCode === "D" ||
        data.internalStatusCode === "X" ||
        data.internalStatusCode === "O") &&
      (data.externalStatusCode === "B" || data.externalStatusCode === "I") &&
      channel === "INB"
    ) {
      messages.add(strings.bankrupt);
    } else if (
      (data.internalStatusCode === "D" || data.internalStatusCode === "X") &&
      (data.externalStatusCode === "" ||
        data.externalStatusCode === "C" ||
        data.externalStatusCode === "E" ||
        data.externalStatusCode === "F" ||
        data.externalStatusCode === "L" ||
        data.externalStatusCode === "U" ||
        data.externalStatusCode === "A") &&
      channel === "INB"
    ) {
      messages.add(strings.a0045a);
    } else if (
      (data.externalStatusCode === "" ||
        data.externalStatusCode === "C" ||
        data.externalStatusCode === "E" ||
        data.externalStatusCode === "F" ||
        data.externalStatusCode === "L" ||
        data.externalStatusCode === "U") &&
      data.internalStatusCode === "O" &&
      channel === "INB"
    ) {
      messages.add(strings.a0045a);
    } else if (
      data.externalStatusCode === "A" &&
      data.internalStatusCode === "O" &&
      channel === "INB"
    ) {
      messages.add(strings.a0044);
    }

    if (data.employeeVipStatus === "Yes") {
      messages.add(strings.vipAccount);
    }

    if (data.externalStatusCode === "L") {
      messages.add(strings.a0110);
    }

    if (data.stateCode === "MA") {
      messages.add(strings.massachusetts);
    }

    if (
      data.externalStatusCode === "Z" &&
      (data.zipcode.startsWith("100") ||
        data.zipcode.startsWith("101") ||
        data.zipcode.startsWith("102") ||
        data.zipcode.startsWith("103") ||
        data.zipcode.startsWith("104") ||
        data.zipcode.startsWith("110") ||
        data.zipcode.startsWith("111") ||
        data.zipcode.startsWith("112") ||
        data.zipcode.startsWith("113") ||
        data.zipcode.startsWith("114") ||
        data.zipcode.startsWith("116"))
    ) {
      messages.add(strings.newYork);
    }

    setVisibleMessages(Array.from(messages));
  }, [data, channel]);

  let debtDisclosureMessage = "";
  switch (debtDisclosureWarning) {
    case "A_0373":
      debtDisclosureMessage = strings.a0373;
      break;
    case "A_0374":
      debtDisclosureMessage = strings.a0374;
      break;
    case "A_0375":
      debtDisclosureMessage = strings.a0375;
      break;
    case "A_0376":
      debtDisclosureMessage = strings.a0376;
      break;
    case "A_0377":
      debtDisclosureMessage = strings.a0377;
      break;
    default:
    // do nothing
  }

  if (isLoading || isError) {
    return null;
  }

  return (
    <>
      {debtDisclosureMessage ? (
        <WarningBanner
          className={classNames(
            styles.debtDisclosureContainer,
            styles.container,
          )}
          message={debtDisclosureMessage}
        />
      ) : null}
      {visibleMessages.map((message, i) => (
        <WarningBanner
          className={styles.container}
          key={message}
          message={message}
          onDismiss={() =>
            setVisibleMessages((prev) => [
              ...prev.slice(0, i),
              ...prev.slice(i + 1),
            ])
          }
        />
      ))}
    </>
  );
}
